import { useState, useRef, useMemo, useCallback, useEffect, forwardRef } from 'react';
import { IconNames } from '@shipengine/giger-theme';
import { useToggle } from 'react-use';
import { useMergeRefs } from 'use-callback-ref';
import { Typography } from '../../Typography/Typography.js';
import { Next } from '../../../next/index.js';
import { RightIcon } from './RightIcon.js';
import { getOptionStyles, getSelectAutocompleteLabelStyles } from './SelectAutocompleteStyles.js';
import { jsxs, Fragment, jsx } from '@emotion/react/jsx-runtime';
import { Input } from '../Input/Input.js';
import { getNewCharacter } from './utils/getNewCharacter.js';
import { DropdownOptionList } from '../../DropdownOptionList/DropdownOptionList.js';
import { Option } from '../../Option/Option.js';

const defaultAriaLabels = {
  toggleOpen: 'Toggle Select'
};
function SelectAutocompleteForwarded(_ref, ref) {
  let {
    autoComplete = 'off',
    disabled,
    dropdownWidth,
    freeInput = false,
    leftContent,
    isOpen,
    keyId,
    label,
    maxVisibleOptions = 5,
    name,
    noResultsLabel,
    optionsHeaderText,
    options,
    placeholder,
    placement = 'bottom-start',
    readOnly,
    showRightIcon = true,
    value,
    hideWhenReferenceNotVisible = true,
    ariaLabels,
    matchOptions,
    searchFn,
    onChange,
    onClear,
    onClickAway,
    onInputChange,
    ...rest
  } = _ref;
  const [query, setQuery] = useState();
  const [editionMode, setEditionMode] = useState(false);
  const [selectedInputValue, setSelectedInputValue] = useState((value === null || value === void 0 ? void 0 : value.label) || '');
  const inputRef = useRef(null);
  const mergedInputRef = useMergeRefs([inputRef, ref]);
  const dropdownOptionListRef = useRef(null);
  const [isDropdownOpen, toggle] = useToggle(false);
  const externalValue = useMemo(() => (value === null || value === void 0 ? void 0 : value.label) || '', [value === null || value === void 0 ? void 0 : value.label]);
  const selectAriaLabels = {
    ...defaultAriaLabels,
    ...ariaLabels
  };
  const filteredOptions = useMemo(() => {
    if (!query) return options;
    return options.filter(option => {
      const parts = searchFn ? searchFn(option.label, query) : Next.defaultHighlightFn(option.label, query, matchOptions);
      return parts.filter(part => part.highlight).length > 0;
    });
  }, [options, query, searchFn, matchOptions]);
  const showDropdown = useMemo(() => !readOnly && !disabled && (!freeInput || freeInput && filteredOptions.length > 0) && isDropdownOpen, [readOnly, disabled, filteredOptions.length, freeInput, isDropdownOpen]);
  const handleOnClick = useCallback(() => {
    if (disabled || readOnly) {
      return;
    }
    toggle();
  }, [disabled, readOnly, toggle]);
  useEffect(() => {
    if (isOpen) {
      handleOnClick();
    }
  }, [handleOnClick, isOpen, name]);
  const handleClickAway = event => {
    var _mergedInputRef$curre;
    // If we click "inside" the SelectAutocomplete do not close the DropdownOptionList
    if ((_mergedInputRef$curre = mergedInputRef.current) !== null && _mergedInputRef$curre !== void 0 && _mergedInputRef$curre.contains(event.target)) {
      return;
    }
    const value = selectedInputValue === null || selectedInputValue === void 0 ? void 0 : selectedInputValue.toLocaleLowerCase();
    const optionSelected = options.find(option => option.value.toLocaleLowerCase() === value || option.label.toLocaleLowerCase() === value);
    if (!freeInput && autoComplete === 'off' && optionSelected === undefined) {
      setSelectedInputValue('');
    }
    onClickAway === null || onClickAway === void 0 ? void 0 : onClickAway(selectedInputValue);
    setQuery('');
    setEditionMode(false);
    isDropdownOpen && toggle();
  };
  const handleOnChange = value => {
    // When the component is inside a form, on Enter key triggers the onchange of the Select.
    // if no option is selected, value is null so when this component is in freeInput we set the value of the input
    const selectedValue = freeInput && !value ? createOption(selectedInputValue) : value;

    // If we edit the input and no option corresponds to it, we need to avoid having an empty value when clicking on enter
    if (selectedValue && selectedValue.value.length > 0) {
      // If there is only one filtered option, set it as the onChange value
      if (filteredOptions.length === 1) {
        onChange === null || onChange === void 0 ? void 0 : onChange(name, filteredOptions[0]);
      } else {
        onChange === null || onChange === void 0 ? void 0 : onChange(name, selectedValue);
      }
    }
    setQuery('');
    setEditionMode(false);
    toggle();
  };
  const handleOnInputChange = event => {
    let value = event.target.value;
    if (!editionMode) {
      setEditionMode(true);
      const externalValueLength = externalValue.length;
      const valueLength = value.length;
      if (valueLength > externalValueLength && externalValueLength > 0) {
        //Case 1: Editing selected value (not for empty field cases)
        value = getNewCharacter(externalValue, value);
      } else if (externalValueLength > 0) {
        //Case 2: We delete the current input
        value = '';
        !freeInput && (onChange === null || onChange === void 0 ? void 0 : onChange(name, null));
      }
      setSelectedInputValue('');
    }

    // As we are controlling the Input value from here
    // we should return the change event with the proper value
    onInputChange === null || onInputChange === void 0 ? void 0 : onInputChange({
      ...event,
      target: {
        ...event.target,
        value
      }
    });
    setSelectedInputValue(value);
    setQuery(value);
    freeInput && (onChange === null || onChange === void 0 ? void 0 : onChange(name, createOption(value)));
    !isDropdownOpen && toggle();
  };
  const handleOnClear = event => {
    onChange === null || onChange === void 0 ? void 0 : onChange(name, null);
    onClear === null || onClear === void 0 ? void 0 : onClear(event);
    setSelectedInputValue('');
    setQuery('');
  };
  const handleOnEscape = () => {
    toggle();
    setEditionMode(false);
  };
  const handleOnKeyDown = event => {
    var _dropdownOptionListRe;
    isDropdownOpen && ((_dropdownOptionListRe = dropdownOptionListRef.current) === null || _dropdownOptionListRe === void 0 ? void 0 : _dropdownOptionListRe.onKeyDown(event));
  };
  return jsxs(Fragment, {
    children: [jsx(Input, {
      autoComplete: autoComplete,
      leftContent: leftContent,
      disabled: disabled,
      label: label,
      name: name,
      placeholder: placeholder,
      readOnly: readOnly,
      ref: mergedInputRef,
      rightContent: showRightIcon && jsx(RightIcon, {
        disabled: disabled || readOnly,
        buttonLabel: selectAriaLabels.toggleOpen,
        iconName: isDropdownOpen && showDropdown ? IconNames.CHEVRON_TOP : IconNames.CHEVRON_BOTTOM,
        onClick: handleOnClick
      }),
      value: editionMode ? selectedInputValue : externalValue,
      onChange: handleOnInputChange,
      onClear: onClear && handleOnClear,
      onClick: handleOnClick,
      onKeyDown: handleOnKeyDown,
      ...rest
    }), jsx(DropdownOptionList, {
      ref: dropdownOptionListRef,
      dropdownWidth: dropdownWidth,
      isOpen: showDropdown,
      optionsHeaderText: optionsHeaderText,
      placement: placement,
      reference: mergedInputRef.current,
      maxVisibleOptions: maxVisibleOptions,
      onChange: handleOnChange,
      onClickAway: handleClickAway,
      onEscape: handleOnEscape,
      hideWhenReferenceNotVisible: hideWhenReferenceNotVisible,
      children: filteredOptions.length > 0 ? filteredOptions.map((item, index) => jsx(Option, {
        value: item,
        selected: (value === null || value === void 0 ? void 0 : value.value) === item.value,
        hasSublabel: !!item.sublabel,
        children: jsxs("div", {
          css: getOptionStyles,
          children: [item === null || item === void 0 ? void 0 : item.leftContent, jsxs("div", {
            css: getSelectAutocompleteLabelStyles(!!(item !== null && item !== void 0 && item.leftContent)),
            children: [jsx(Next.Highlight, {
              searchFn: searchFn,
              matchOptions: matchOptions,
              query: query,
              translate: "no",
              children: item.label
            }), item.sublabel && jsx(Typography, {
              variant: "small",
              children: item.sublabel
            })]
          })]
        })
      }, keyId ? item[keyId] : index)) : jsx(Option, {
        value: "",
        children: noResultsLabel
      })
    })]
  });
}
SelectAutocompleteForwarded.displayName = 'SelectAutocomplete';
function createOption(value) {
  return {
    label: value,
    value
  };
}
const SelectAutocomplete = /*#__PURE__*/forwardRef(SelectAutocompleteForwarded);

export { SelectAutocomplete, SelectAutocompleteForwarded };
//# sourceMappingURL=SelectAutocomplete.js.map
